import React from 'react';
import { StatsCards, StatCard } from '../../components/Common';

interface DocumentItem {
  documentType: string;
  documentNumber: string;
  issueDate?: string;
  expiryDate?: string;
  issuingAuthority?: string;
  status: string;
}

interface DocumentDetailsScreenProps {
  employeeName: string;
  employeeCode: string;
  employeeRank: string;
  documents: DocumentItem[];
  onBack: () => void;
}

const getStatusBadge = (status: string) => {
  if (status === 'expired') return { className: 'bg-red-100 text-red-800', text: 'EXPIRED' };
  if (status === 'expiring_soon') return { className: 'bg-yellow-100 text-yellow-800', text: 'EXPIRING SOON' };
  return { className: 'bg-green-100 text-green-800', text: 'Valid' };
};

const DocumentDetailsScreen: React.FC<DocumentDetailsScreenProps> = ({
  employeeName, employeeCode, employeeRank, documents, onBack,
}) => {
  const expiredCount = documents.filter(d => d.status === 'expired').length;
  const expiringSoonCount = documents.filter(d => d.status === 'expiring_soon').length;

  const stats: StatCard[] = [
    {
      label: 'Total Documents',
      value: documents.length,
      color: 'blue',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      label: 'Expiring Soon',
      value: expiringSoonCount,
      color: 'yellow',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    },
    {
      label: 'Expired',
      value: expiredCount,
      color: 'red',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Crew and Cars Documents
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Document Details</h1>
            <p className="text-lg text-gray-600 mt-1">
              {employeeName} ({employeeCode}) - {employeeRank}
            </p>
          </div>
        </div>
      </div>

      <StatsCards stats={stats} columns={3} />

      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">All Documents</h2>
        </div>
        <div className="p-6">
          {documents.length > 0 ? (
            <div className="space-y-4">
              {documents.map((doc, index) => {
                const badge = getStatusBadge(doc.status);
                return (
                  <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-xl font-semibold text-gray-900">{doc.documentType}</h3>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badge.className}`}>
                            {badge.text}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-700 mb-1">Document Number</span>
                            <span className="text-gray-900 text-base">{doc.documentNumber}</span>
                          </div>
                          {doc.issueDate && (
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-700 mb-1">Issue Date</span>
                              <span className="text-gray-900 text-base">{new Date(doc.issueDate).toLocaleDateString()}</span>
                            </div>
                          )}
                          {doc.expiryDate && (
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-700 mb-1">Expiry Date</span>
                              <span className="text-gray-900 text-base">{new Date(doc.expiryDate).toLocaleDateString()}</span>
                            </div>
                          )}
                          {doc.issuingAuthority && (
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-700 mb-1">Issuing Authority</span>
                              <span className="text-gray-900 text-base">{doc.issuingAuthority}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-4 text-lg text-gray-600">No documents found for this employee</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentDetailsScreen;
